import { useSelector } from 'react-redux';
import { Icon, Surface, Tooltip, Txt } from '@vst/beam';
import { Info } from '@vst/beam-icons/icons';
import { useTranslation } from 'react-i18next';

import { formatPriceWithCurrency, selectChangePlan } from '@app/redux/changePlan';
import { selectLocale } from '@app/redux/locale';

import { GetPlanOffersPayload } from '@mfe/shared/schema-types';

import styles from './styles.module.scss';

type OneTimeFeesProps = {
  selectedPlan: GetPlanOffersPayload;
};

const OneTimeFees = ({ selectedPlan }: OneTimeFeesProps) => {
  const { t } = useTranslation('ChangePlan', {
    keyPrefix: 'orderReview.cartDetails',
  });

  const {
    locale: { userLocale },
  } = useSelector(selectLocale);

  const {
    cart: {
      prices: {
        oneTime: { feesAndDiscounts, totalWithoutPromos },
      },
    },
  } = useSelector(selectChangePlan);

  const oneTimeTotal = totalWithoutPromos
    ? totalWithoutPromos
    : feesAndDiscounts?.reduce((acc, item) => acc + (item.total ?? 0), 0) ?? 0;

  const { priceCurrency } = selectedPlan;

  return (
    <div>
      {feesAndDiscounts && feesAndDiscounts?.length > 0 ? (
        <div data-cy="one-time-fees" className={styles['container']}>
          <Surface variant="primary" radius="8px" style={{ display: 'flex', flexDirection: 'column' }}>
            <div data-cy="one-time-fees-title" className={styles['one-time-fees']}>
              <Txt variant="bodySmallBold">{t('oneTimeFees')}</Txt>
              <Tooltip
                data-cy="internet-product-tooltip-text"
                bodyText={t('cartSummary.oneTimeFeesTooltip')}
                beakPosition="center"
              >
                <Icon icon={Info} color="gray_600" size="16px" style={{ marginLeft: '4px' }} />
              </Tooltip>
            </div>

            <Txt variant="bodySmallRegular" data-cy="one-time-fees-pricing" mb="16px" mt="8px">
              {t('oneTimeFeesInfo')}
            </Txt>

            <div data-cy="one-time-fees-description" className={styles['one-time-fees-list']}>
              {feesAndDiscounts?.map((feeAndDiscount, index) => {
                const hasDiscount = (feeAndDiscount.discount ?? 0) < 0;

                const freeLabel = (
                  <Txt component="span" variant="bodySmallBold" color="success">
                    {t('free')}
                  </Txt>
                );

                const formattedTotalPrice =
                  feeAndDiscount.total === 0
                    ? freeLabel
                    : formatPriceWithCurrency(userLocale, feeAndDiscount.total ?? 0, priceCurrency, 0);

                return (
                  <div className={styles['info']} key={index}>
                    <Txt variant="bodySmallRegular" color="subtle">
                      {feeAndDiscount.name}
                    </Txt>

                    <Txt
                      variant="paragraphBold"
                      data-cy="one-time-fees-total-amount-discount"
                      style={{ textAlign: 'right' }}
                    >
                      <Txt variant="bodySmallBold" color={hasDiscount ? 'success' : 'info'}>
                        {formattedTotalPrice}
                      </Txt>
                      {hasDiscount && (
                        <div style={{ textAlign: 'right', marginBottom: '8px' }}>
                          <Txt
                            color="subtle"
                            component="span"
                            variant="linkBodySmall"
                            style={{
                              textDecoration: 'line-through',
                              textAlign: 'right',
                            }}
                          >
                            {formatPriceWithCurrency(userLocale, feeAndDiscount.fee ?? 0, priceCurrency, 0)}
                          </Txt>
                        </div>
                      )}
                    </Txt>
                  </div>
                );
              })}
            </div>

            <hr style={{ marginTop: 8 }} />

            <Surface data-cy="one-time-fees-total-section" className={`${styles['info']} `} mt="16px">
              <Txt variant="heading6">{t('oneTimeFeesTotal')}</Txt>
              <Txt variant="paragraphBold" data-cy="one-time-fees-total-amount-value">
                <Txt color="subtle" component="span" variant="paragraphBold">
                  {formatPriceWithCurrency(userLocale, oneTimeTotal ?? 0, priceCurrency, 0)}
                </Txt>
              </Txt>
            </Surface>
          </Surface>
        </div>
      ) : (
        <hr />
      )}
    </div>
  );
};

export default OneTimeFees;
